import axios from 'axios';
import { toast } from 'react-toastify';
import * as sessao from './platform/storage';
import { autorizacaoDaRequisicao } from './platform/autorizacao';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001',
});

// 🔹 Anexa JWT em toda request
api.interceptors.request.use(
    (config) => {
        // Nao sobrescreve credencial escolhida por quem chama: a renovacao da
        // sessao manda o REFRESH token, e o access guardado nessa hora esta
        // vencido. Sobrescrever fazia o servidor responder 401 e o cliente
        // jogar fora um refresh de 30 dias. Ver platform/autorizacao.js.
        const autorizacao = autorizacaoDaRequisicao({
            jaDefinido: config.headers?.Authorization,
            tokenGuardado: sessao.getItem('token'),
        });
        if (autorizacao) {
            config.headers = config.headers || {};
            config.headers.Authorization = autorizacao;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Guarda estado do fluxo de refresh pra:
//   1. Nao rodar N refreshes em paralelo quando 10 requests batem 401 juntas
//   2. Nao mostrar N toasts de sessao expirada
//   3. Nao redirecionar mais de uma vez
let refreshPromise = null;
let sessaoExpiradaAvisada = false;

const isPublicPath = () => {
    const p = window.location.pathname;
    // NAO inclui '/' — a home '/' redireciona pra Dashboard quando autenticado,
    // e se o token expira ali um 401 vem sem qualquer aviso pro usuario. Com '/'
    // fora da lista, clearAndRedirect mostra o toast "sessao expirou" e salva
    // returnTo. Landing publica em '/' ainda funciona: nao dispara 401 porque
    // nao chama api.
    return (
        p === '/login' ||
        p === '/register' ||
        p.startsWith('/esqueci-senha') ||
        p.startsWith('/resetar-senha') ||
        p.startsWith('/ajuda')
    );
};

// Reseta o gate do toast de sessao expirada. Chamado no login pra o proximo
// ciclo (login -> uso -> token expira) poder mostrar o toast de novo.
export const resetarAvisoSessaoExpirada = () => {
    sessaoExpiradaAvisada = false;
};

const clearAndRedirect = ({ silent = false } = {}) => {
    // Salva pra onde voltar depois do login, se nao for rota publica
    if (!isPublicPath()) {
        try {
            const returnTo = window.location.pathname + window.location.search;
            sessionStorage.setItem('post_login_redirect', returnTo);
        } catch { /* sessionStorage cheio ou desativado: segue sem */ }
    }
    sessao.removeItem('token');
    sessao.removeItem('refresh_token');
    sessao.removeItem('is_admin');
    sessao.removeItem('tenant');
    sessao.removeItem('is_superadmin');

    if (!silent && !sessaoExpiradaAvisada && !isPublicPath()) {
        sessaoExpiradaAvisada = true;
        toast.info('Sua sessão expirou. Faça login novamente.', {
            autoClose: 3000, position: 'top-center',
        });
        // Delay pequeno pra o toast aparecer antes do redirect
        setTimeout(() => { window.location.href = '/login'; }, 800);
    } else if (!isPublicPath()) {
        window.location.href = '/login';
    }
};

// Faz o refresh — deduplicado. Se ja existe um refresh em voo, todos aguardam
// o mesmo. Retorna o novo access_token ou lanca.
const executarRefresh = async () => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const refreshToken = sessao.getItem('refresh_token');
        if (!refreshToken) throw new Error('sem_refresh_token');
        // Usa axios direto (nao `api`) pra nao passar pelo response interceptor
        // e cair em loop infinito quando o proprio /refresh der 401.
        const resp = await axios.post(
            `${api.defaults.baseURL}/refresh`,
            {},
            { headers: { Authorization: `Bearer ${refreshToken}` } }
        );
        const novoToken = resp.data.access_token;
        if (!novoToken) throw new Error('resposta_sem_token');
        sessao.setItem('token', novoToken);
        return novoToken;
    })();

    try {
        return await refreshPromise;
    } finally {
        // Libera pra proxima onda (se necessario) — o token ficara valido
        // por um tempo, entao proximos 401 na sequencia sao raros.
        refreshPromise = null;
    }
};

// 🔹 Trata 401 com refresh + fallback pra login
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if (status === 401 && originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;
            // Marca que fomos NOS que setamos _retry — pra distinguir do caller
            // que seta _retry pra bypassar refresh (logout, /refresh).
            originalRequest._interceptorRetried = true;
            try {
                const novoToken = await executarRefresh();
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${novoToken}`;
                return api(originalRequest);
            } catch {
                clearAndRedirect();
                return Promise.reject(error);
            }
        }

        // 401 apos retry do interceptor (refresh deu token novo mas backend
        // ainda rejeita — ex: user deletado, tenant desativado, JWT_SECRET_KEY
        // rotacionado): limpa credenciais e manda pro login. Antes ficava
        // passando 401 silenciosamente pro caller.
        // NAO dispara pra _retry setado pelo caller (logout / /refresh), senao
        // um logout com token expirado gera toast "sessao expirou" indevido.
        if (status === 401 && originalRequest?._interceptorRetried) {
            clearAndRedirect();
            return Promise.reject(error);
        }

        // Log de erros nao-401 pra debug (nao floodar console em 401)
        if (status && status !== 401) {
            const url = originalRequest?.url || '?';
            const msg = error.response?.data?.erro || error.response?.data?.error || error.message;
            console.error(`[API ${status}] ${url}: ${msg}`);
        }

        return Promise.reject(error);
    }
);

export default api;
